/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/**
 * BasicMap is a simple template to
 * render a single data source.
 * Currently supports only point data
 */

import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import PropTypes from 'prop-types';
import { useIntersect } from 'sarsaparilla';
import { processPoints } from '../helpers/parseFeatures';
import { createEmptyCollection } from '../helpers/utils';
import { getLayersPerService } from '../layers/layers';
import { POINT } from '../constants';
import { getPopupContentByService } from '../helpers/mapPopup';
import { addMapControls, getControlsPerService } from '../controls/controls';
import {
    cursorToDefault,
    cursorToPointer,
    fitMapBounds,
    trackIsMapExtended,
} from '../helpers/common';
import LoadingSpinner from '../components/LoadingSpinner';

function BasicMap(props) {
    const mapContainer = useRef(props.mapContainer || 'map');
    const map = useRef(null);
    const basicControls = useRef(null);
    const basicControlsList = useRef(getControlsPerService(props.service));

    const [geojson, setGeojson] = useState(props.geojson || {});
    const [processedGeojson, setProcessedGeojson] = useState({});
    const [layerSetup, setLayerSetup] = useState({});
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [isMapExtended, setIsMapExtended] = useState(false);

    const [ref, entry] = useIntersect({ rootMargin: '100px' });

    let popup;

    // Basic map allows only one source
    const sourceName = 'mapSource';

    // Update service setup params
    useEffect(() => {
        const layerParams = {
            service: props.service,
            source: sourceName,
        };
        setLayerSetup(getLayersPerService(layerParams));
    }, [props.service]);

    useEffect(() => {
        if (!map.current || !basicControlsList.current?.gesture) return;
        map.current._cooperativeGestures = isMapExtended
            ? false
            : basicControlsList.current?.gesture;
    }, [isMapExtended]);

    const addMapSources = () => {
        if (!map.current) return;
        map.current.addSource(sourceName, createEmptyCollection({ cluster: false }));
    };

    const addMapLayers = () => {
        if (!map.current) return;

        // eslint-disable-next-line no-unused-expressions
        layerSetup?.layers.forEach((layer) => {
            map.current.addLayer(layer);
        });
    };

    const setPopup = (feature) => {
        if (popup) popup.remove();
        popup = new mapboxgl.Popup({
            maxWidth: 'none',
            closeButton: false,
        });

        popup
            .setDOMContent(getPopupContentByService(feature, props.service))
            .setLngLat(feature.geometry.coordinates)
            .addTo(map.current);
    };

    const addMapEvents = () => {
        map.current.on('load', () => {
            setIsMapLoaded(true);

            addMapSources();
            addMapLayers();

            // eslint-disable-next-line no-unused-expressions
            layerSetup?.layers?.forEach((layer) => {
                map.current.on('mouseenter', layer.id, () => cursorToPointer(map));
                map.current.on('mouseleave', layer.id, () => cursorToDefault(map));
            });
        });

        if (props?.selectedFeatureId) {
            const selectedFeature = geojson?.features.find(
                (item) => item?.properties?.id === props?.selectedFeatureId
            );
            if (selectedFeature) {
                setPopup(selectedFeature);
            }
        }

        // Track double click
        let clicks = 0;

        map.current.on('click', (e) => {
            const point = e.point;
            clicks = 0;
            setTimeout(() => {
                if (!clicks) {
                    const features = map.current.queryRenderedFeatures(point, {
                        layers: layerSetup?.layers.map((layer) => layer.id),
                    });
                    const feature = features?.length ? features[0] : null;
                    if (feature) setPopup(feature);
                }
            }, 250);
        });

        map.current.on('dblclick', () => {
            clicks += 1;
        });
    };

    const initMap = () => {
        if (!map.current) return;
        basicControls.current = addMapControls(map, basicControlsList.current);

        if (basicControlsList.current?.gesture) {
            trackIsMapExtended(basicControls.current, setIsMapExtended);
        }
        addMapEvents();
    };

    // Initiates map if it was not initiated already
    useEffect(() => {
        if (!map.current && entry?.isIntersecting) {
            mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN;
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: process.env.MAPBOX_STYLE,
                center: props.center,
                zoom: props.zoom,
                attributionControl: false,
                cooperativeGestures: basicControlsList.current?.gesture,
                preserveDrawingBuffer: true,
            });
            initMap();
        }
    }, [entry?.isIntersecting]);

    // Update provided geojson data
    useEffect(() => {
        setGeojson(props.geojson);
    }, [props.geojson]);

    const getGeojson = (features) => {
        let processed;
        if (!features?.features?.length) return processed;
        if (layerSetup?.dataType === POINT) processed = processPoints(features);

        return processed;
    };

    // Process geojson when updated
    useEffect(() => {
        const newGeojson = getGeojson(geojson);
        setProcessedGeojson(newGeojson);
    }, [geojson, isMapLoaded]);

    const setMapSource = () => {
        if (map.current && processedGeojson) {
            const mapSource = map.current.getSource(sourceName);
            if (map.current.getSource(sourceName)) {
                mapSource.setData(processedGeojson);
                if (!props.skipMapFitBounds) {
                    const fitZoom = props.defaultMaxZoom ? undefined : props.zoom;
                    fitMapBounds(
                        map,
                        processedGeojson,
                        fitZoom,
                        props.defaultMaxZoom,
                        props.checkUSBoundary
                    );
                }
            }
        }
    };

    // Update map sources
    useEffect(() => {
        setMapSource();
    }, [processedGeojson]);

    return <LoadingSpinner mapRef={ref} isMapLoaded={isMapLoaded} />;
}

BasicMap.propTypes = {
    mapContainer: PropTypes.string,
    service: PropTypes.string,
    geojson: PropTypes.object,
    selectedFeatureId: PropTypes.string,
    center: PropTypes.array,
    zoom: PropTypes.number,
    featureName: PropTypes.string,
    skipMapFitBounds: PropTypes.bool,
    defaultMaxZoom: PropTypes.number,
    checkUSBoundary: PropTypes.bool,
};

export default BasicMap;
